
type GenericFunc = {
    (...args: any): any
}

export class CustomController {

    listeners: Record<string, (GenericFunc | GenericFunc[] | undefined)> = {};
    debug: boolean = false;
    verbose: boolean = false;

    /**
     * 
     * @param {string} elementId The id or class of the element.
     * @param {keyof HTMLElementEventMap} eventType 
     * @param {string} key The key added to the eventListenerId.
     */
    _getEventListenerId(elementId: string, eventType: keyof HTMLElementEventMap | string, key?: string): string {
        if (key) {
            return `${eventType}@${elementId}#${key}`;
        }
        return `${eventType}@${elementId}`;
    }

    /**
     * 
     * @param {string} elementId The id or class of the element.
     * @param {string} key The key added to the eventListenerId.
     * @returns {[HTMLElement, boolean] | null} Returns null on error.
     */
    _getElement(elementId: string, key?: string): [HTMLElement, boolean] | null {
        let elem: HTMLElement | HTMLCollection | null = document.getElementById(elementId);
        let isClass = false;

        if (!elem) {
            // Try to check if the given ID is a class.
            elem = document.getElementsByClassName(elementId);
            if (!elem || elem.length === 0) {
                this.debug && console.warn(`No element with id or class, '${elementId}' was found !`);
                return null;
            }

            var index = 0;
            var keyUsedAsIndex = false;
            if (key) {
                if (!isNaN(parseInt(key))) {
                    index = parseInt(key);
                    keyUsedAsIndex = true;
                }
            }
            var msg = `Found elements of class '${elementId}', using element at index '${index}'`;
            if (!keyUsedAsIndex) msg += `.\n\tUse an id or add a (numerical) key to change this behaviour !`;
            this.debug && console.warn(msg);
            if (elem.length < index) {
                this.debug && console.warn("Parsed index is higher than the number of elements, defaulting to index '0'.");
                index = 0;
            }
            elem = elem[index] as HTMLElement;
            isClass = true;
        }
        return [elem, isClass];
    }

    /**
     * 
     * @param {string} key Represents the key in the this.listeners object.
     * @returns {[string, string, keyof HTMLElementEventMap | string] | null} Returns null on error.
     */
    _getInfoFromKey(key: string): [string, string, keyof HTMLElementEventMap | string] | null {
        const regex = /(?<EventType>[a-z]*)@(?<ID>[a-z](?:[a-z0-9\-\_])+)#?(?<KEY>[a-z0-9]+)?/i;
        const result = key.match(regex);

        if (!result || !result.groups) {
            console.error(`No match of regex '${regex}' on key '${key}' !\n\t\tDoes the id start with a number ?`);
            return null;
        }

        const elemId = result.groups['ID'];
        const eventType = result.groups['EventType'] as keyof HTMLElementEventMap | string;
        const k = result.groups['KEY'];
        return [elemId, k, eventType];
    }

    /**
     * This handles adding an event listener, 
     * if the element isn't visible the listener won't be added by default.
     * 
     * @param {keyof HTMLElementEventMap | string} eventType 
     * @param {GenericFunc} listener 
     * @param {string} elementId The id or class of the element.
     * @param {string|undefined} key The key added to the eventListenerId.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    addEventListener(
        eventType: keyof HTMLElementEventMap | string, listener: (...args: any) => any,
        elementId: string, key?: string,
        ignoreVisibility?: boolean) {

        let elemInfo = this._getElement(elementId, key);
        if (elemInfo === null) return;
        let [elem, isClass] = elemInfo;

        if (!ignoreVisibility && !elem.checkVisibility()) return;

        elem.addEventListener(eventType, listener);
        const evtListId = this._getEventListenerId(elementId, eventType, key);
        let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
        let isArray = Array.isArray(entry);

        if (!isClass && !isArray) {
            if (entry) {
                console.warn(`Already added a listener of type '${eventType}' to element with id '${elementId}' !\n\t Overwriting old listener !`);
            }
            this.listeners[evtListId] = listener;
        } else {
            if (entry) {
                if (key) {
                    this.debug && console.warn(`Found a key, overwriting existant entry for id '${evtListId}' !`);
                    this.listeners[evtListId] = [listener];
                } else {
                    this.debug && console.warn(`Found existant entry(-ies) for '${evtListId}', appending current entry...`);
                    if (Array.isArray(this.listeners[evtListId])) {
                        (this.listeners[evtListId] as GenericFunc[]).push(listener);
                    } else {
                        this.listeners[evtListId] = listener;
                    }
                }
            } else {
                this.debug && console.log(`No entry found for '${evtListId}', creating one...`);
                let evtListeners = [listener];
                this.listeners[evtListId] = evtListeners;
            }
        }

        this.debug || this.verbose && console.log(`Added '${evtListId}' !`);
    }

    /**
     * This handles removing an event listener, 
     * if the element isn't visible the listener won't be removed by default.
     * 
     * @param {keyof HTMLElementEventMap | string} eventType 
     * @param {string} elementId The id or class of the element.
     * @param {string|undefined} key The key added to the eventListenerId.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    removeEventListener(
        eventType: keyof HTMLElementEventMap | string, elementId: string, 
        key?: string, ignoreVisibility?: boolean) {

        let elemInfo = this._getElement(elementId, key);
        const evtListId = this._getEventListenerId(elementId, eventType, key);

        if (elemInfo === null) {
            if (this.listeners[evtListId] !== undefined) {
                this.listeners[evtListId] = undefined;
                this.debug && console.warn(`No element found for id '${elementId}' but the key '${evtListId}' exists in listener list.`);
            }
            return 0;
        }
        let [elem, isClass] = elemInfo;
        let nbRemoved = 0;

        if (!ignoreVisibility && !elem.checkVisibility()) return 0;

        
        let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
        let isArray = Array.isArray(entry);

        if (!isClass && !isArray) {
            let evtList = entry as (GenericFunc | undefined);

            if (!evtList) {
                console.warn(`There are no eventListener that matches '${evtListId}', did you call remove by accident ?`);
            } else {
                elem.removeEventListener(eventType, evtList);
                nbRemoved++;
            }
        } else {
            let evtListeners = entry as (GenericFunc[] | undefined);

            if (!evtListeners || evtListeners.length === 0) {
                console.warn(`There are no eventListeners that match '${evtListId}', did you call remove by accident ?`);
            } else {
                var msg = "Warning ! Calling removeEventListener on a class removes EVERY eventListener of that class !";
                msg += `\n\tUse an id or a key to change this behaviour !`;
                !key && console.warn(msg);
                for (let i = 0; i < evtListeners.length; i++) {
                    let evtListener = evtListeners[i];
                    elem.removeEventListener(eventType, evtListener);
                    nbRemoved++;
                }
            }
        }
        this.listeners[evtListId] = undefined;

        this.debug || this.verbose && console.log(`Removed '${evtListId}' !`);
        return nbRemoved;
    }

    destroy() {
        this.debug && console.log("Destroy called on controller !\n\tRemoving listeners...");

        let i = 0;
        let keys = Object.keys(this.listeners)
        if (keys.length !== 0) {
            keys.forEach((key) => {
                if (this.listeners[key] === undefined) {
                    this.verbose && console.warn(`Empty entry at key '${key}', skipping...`);
                    return;
                }
                let infos = this._getInfoFromKey(key);
                if (infos === null) return;
                let [elemId, keyString, eventType] = infos;
                i += this.removeEventListener(eventType, elemId, keyString, true);
                this.listeners[key] = undefined;
            });
        }

        console.log(`Destroy finished.\n\tRemoved ${i} listener(s).`);
        this.listeners = {};
    }
}